import { useMemo } from 'react';
import { PawPrint, Syringe } from 'lucide-react';
import type { Pet, PetRecord } from '@app/shared';
import type { AttentionItem, SearchResult, WebModule } from '../../app/types.ts';
import { useRecords } from '../../platform/data/repo.ts';
import { familyRecords, scoreText } from '../../platform/search.ts';
import { daysUntil, dueLabelInline } from '../../platform/time.ts';

function usePetAttention(): AttentionItem[] {
  const pets = useRecords<Pet>('pets');
  const records = useRecords<PetRecord>('pet_records');
  return useMemo(() => {
    const names = new Map((pets ?? []).filter((p) => !p.archived).map((p) => [p.id, p.name]));
    return (records ?? [])
      .filter((r) => r.dueDate && names.has(r.petId) && daysUntil(r.dueDate) <= 14)
      .map((r) => ({
        key: `pet_record:${r.id}`,
        title: `${names.get(r.petId)}: ${r.title}`,
        detail: `${r.kind === 'vaccination' ? 'Vaccination' : 'Medication'} due ${dueLabelInline(r.dueDate!)}`,
        to: `/pets/${r.petId}`,
        tone: daysUntil(r.dueDate!) < 0 ? ('danger' as const) : ('warning' as const),
        icon: Syringe,
        days: daysUntil(r.dueDate!),
      }));
  }, [pets, records]);
}

export const petsModule: WebModule = {
  id: 'pets',
  label: 'Pets',
  icon: PawPrint,
  path: '/pets',
  nav: { group: 'household', order: 20 },
  routes: [
    { path: 'pets', lazy: async () => ({ Component: (await import('./PetsScreen.tsx')).PetsScreen }) },
    { path: 'pets/:petId', lazy: async () => ({ Component: (await import('./PetScreen.tsx')).PetScreen }) },
  ],
  useAttention: usePetAttention,
  async search(q, ctx) {
    const out: SearchResult[] = [];
    const pets = await familyRecords<Pet>('pets', ctx.familyId);
    const names = new Map(pets.map((p) => [p.id, p.name]));
    for (const p of pets) {
      const score = scoreText(q, [p.name, p.breed, p.vet.name, p.vet.clinic, p.microchipId, p.notes]);
      if (score) out.push({ key: `pets:${p.id}`, title: p.name, subtitle: p.breed || 'Pet', to: `/pets/${p.id}`, group: 'Pets', icon: PawPrint, score });
    }
    for (const r of await familyRecords<PetRecord>('pet_records', ctx.familyId)) {
      const score = scoreText(q, [r.title, r.notes, r.provider]);
      if (score && names.has(r.petId))
        out.push({
          key: `pet_records:${r.id}`,
          title: r.title,
          subtitle: `${names.get(r.petId)} · ${r.kind}`,
          to: `/pets/${r.petId}`,
          group: 'Pets',
          icon: PawPrint,
          score,
        });
    }
    return out;
  },
};
